import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";
import { inrFull } from "@/lib/format";
import { Download } from "lucide-react";
import { TOKEN_KEY } from "@/lib/api";

const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Payslips() {
  const { data: slips = [], isLoading } = useQuery({ queryKey: ["payslips"], queryFn: async () => (await api.get("/payslips")).data });

  const download = async (id) => {
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/payslips/${id}/pdf`;
    const res = await fetch(base, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payslip-${id}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="payslips-page">
      <PageHeader title="Payslips" subtitle={`${slips.length} payslips`} />
      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
        <Table data-testid="payslips-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
              <TableHead className="uppercase text-xs tracking-wider">Period</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Employee</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right">Present / Working</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right">Net Pay</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Status</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right">PDF</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 4 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)}
            {!isLoading && slips.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No payslips yet.</TableCell></TableRow>}
            {!isLoading && slips.map((s) => {
              const net = s.net_pay ?? s.net ?? (s.earnings && s.deductions ? Object.values(s.earnings).reduce((a, b) => a + (Number(b) || 0), 0) - Object.values(s.deductions).reduce((a, b) => a + (Number(b) || 0), 0) : null);
              return (
                <TableRow key={s.id} data-testid={`payslip-row-${s.id}`}>
                  <TableCell className="font-medium text-slate-900 dark:text-slate-100">{MONTHS[s.month]} {s.year}</TableCell>
                  <TableCell className="text-slate-600 dark:text-slate-300">{s.user_name}<div className="text-xs text-slate-400">{s.role} · {s.employee_id}</div></TableCell>
                  <TableCell className="text-right font-num">{s.present_days ?? "—"} / {s.working_days ?? "—"}</TableCell>
                  <TableCell className="text-right font-num font-semibold">{net != null ? inrFull(net) : "—"}</TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => download(s.id)} data-testid={`payslip-download-${s.id}`}><Download className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
